import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users2, Building2, Church, Users, Briefcase, Plus, Search, ArrowRight, Layers,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import ImageUploader from "@/components/ImageUploader";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PAGE_TYPE_META = {
  empresa:         { label: "Empresa",         icon: Building2, color: "text-blue-600 bg-blue-500/10" },
  igreja:          { label: "Igreja",          icon: Church,    color: "text-purple-600 bg-purple-500/10" },
  grupo_trabalho:  { label: "Grupo de Trabalho", icon: Briefcase, color: "text-amber-600 bg-amber-500/10" },
  comunidade:      { label: "Comunidade",      icon: Users,     color: "text-emerald-600 bg-emerald-500/10" },
};

function PageCard({ p }) {
  const meta = PAGE_TYPE_META[p.type] || { label: p.type, icon: Layers, color: "text-muted-foreground bg-muted" };
  const Icon = meta.icon;
  return (
    <Link
      to={`/pages/${p.page_id}`}
      className="block"
      data-testid={`page-card-${p.page_id}`}
    >
      <Card className="overflow-hidden hover-lift">
        <div
          className="h-24 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent relative"
          style={p.cover_url ? { backgroundImage: `url(${p.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        >
          <div className={`absolute top-2 left-2 px-2 py-1 rounded-full ${meta.color} text-[10px] font-bold uppercase tracking-widest flex items-center gap-1`}>
            <Icon className="w-3 h-3" /> {meta.label}
          </div>
        </div>
        <div className="p-4 flex gap-3 items-start -mt-8">
          <div className="w-14 h-14 rounded-xl border-4 border-card bg-card overflow-hidden flex-shrink-0">
            {p.avatar_url ? (
              <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <Icon className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pt-6">
            <div className="font-bold truncate">{p.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{p.category || "—"}</div>
            <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
              <Users2 className="w-3 h-3" /> {p.followers_count || 0} seguidores
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function Pages() {
  const { user } = useAuth();
  const [tab, setTab] = useState("browse");
  const [pages, setPages] = useState([]);
  const [mine, setMine] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: "", type: "empresa", description: "", category: "", avatar_url: "", cover_url: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (typeFilter) params.set("type", typeFilter);
    if (q) params.set("q", q);
    const [pubR, mineR] = await Promise.all([
      axios.get(`${API}/pages${params.toString() ? `?${params}` : ""}`),
      axios.get(`${API}/pages/mine`).catch(() => ({ data: [] })),
    ]);
    setPages(pubR.data || []);
    setMine(mineR.data || []);
  };

  useEffect(() => { load(); }, [typeFilter, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Informe um nome");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/pages`, form);
      toast.success("Página criada!");
      setOpenCreate(false);
      setForm({ name: "", type: "empresa", description: "", category: "", avatar_url: "", cover_url: "" });
      setTab("mine");
      load();
      // navigate to manage
      window.location.href = `/pages/${res.data.page_id}/manage`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <SponsoredAdWidget variant="inline" limit={2} />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="pages-heading">Páginas & Grupos</h1>
          <p className="text-muted-foreground text-sm">Crie páginas para empresas, igrejas, grupos de trabalho ou comunidades e gerencie anúncios como o Facebook.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-page-btn" className="rounded-full gap-2">
              <Plus className="w-4 h-4" /> Criar página
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-page-dialog">
            <DialogHeader><DialogTitle>Nova página ou grupo</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(PAGE_TYPE_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm({ ...form, type: key })}
                      data-testid={`page-type-${key}`}
                      className={`p-3 rounded-xl border-2 transition-colors text-left ${
                        form.type === key ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg ${meta.color} flex items-center justify-center mb-2`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="text-sm font-semibold">{meta.label}</div>
                    </button>
                  );
                })}
              </div>
              <Input
                data-testid="new-page-name"
                placeholder="Nome da página (ex: Igreja Batista Central)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                data-testid="new-page-category"
                placeholder="Categoria (ex: Restaurante, Ministério, Marketing…)"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
              <Textarea
                data-testid="new-page-description"
                rows={3}
                placeholder="Descrição / bio"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <div className="flex gap-2">
                <Input
                  data-testid="new-page-avatar"
                  placeholder="URL do avatar"
                  value={form.avatar_url}
                  onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                />
                <ImageUploader testid="new-page-avatar-upload" size="default" label="Avatar" onUploaded={(url) => setForm({ ...form, avatar_url: url })} />
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="new-page-cover"
                  placeholder="URL da capa"
                  value={form.cover_url}
                  onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
                />
                <ImageUploader testid="new-page-cover-upload" size="default" label="Capa" onUploaded={(url) => setForm({ ...form, cover_url: url })} />
              </div>
              <Button data-testid="submit-page" onClick={submit} disabled={saving} className="w-full rounded-full">
                {saving ? "Salvando..." : "Criar página"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full">
          <TabsTrigger value="browse" data-testid="pages-tab-browse" className="rounded-full">Descobrir</TabsTrigger>
          <TabsTrigger value="mine" data-testid="pages-tab-mine" className="rounded-full">
            Minhas ({mine.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-6 space-y-4">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="pages-search"
                placeholder="Buscar páginas..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              size="sm"
              variant={typeFilter === "" ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setTypeFilter("")}
              data-testid="pages-filter-all"
            >
              Todas
            </Button>
            {Object.entries(PAGE_TYPE_META).map(([key, meta]) => (
              <Button
                key={key}
                size="sm"
                variant={typeFilter === key ? "default" : "outline"}
                className="rounded-full gap-1"
                onClick={() => setTypeFilter(key)}
                data-testid={`pages-filter-${key}`}
              >
                <meta.icon className="w-3.5 h-3.5" /> {meta.label}
              </Button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pages.length === 0 && (
              <Card className="p-12 text-center text-muted-foreground col-span-full" data-testid="pages-empty">
                <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                Nenhuma página encontrada. Seja o primeiro a criar!
              </Card>
            )}
            {pages.map((p) => <PageCard key={p.page_id} p={p} />)}
          </div>
        </TabsContent>

        <TabsContent value="mine" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.length === 0 && (
              <Card className="p-12 text-center text-muted-foreground col-span-full" data-testid="my-pages-empty">
                Você ainda não criou nenhuma página. Clique em "Criar página" para começar.
              </Card>
            )}
            {mine.map((p) => (
              <div key={p.page_id} className="relative">
                <PageCard p={p} />
                <Link
                  to={`/pages/${p.page_id}/manage`}
                  data-testid={`manage-page-${p.page_id}`}
                  className="absolute top-2 right-2 z-10 h-8 px-3 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold flex items-center gap-1 hover:opacity-90"
                >
                  Gerenciar <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
